import _ from "underscore";
import Constants from "const/constants";
import moment from "moment";
import { getPasswordStrengthTests, rtmpRegex } from "util/string-utils";
import { isValidStreamUrl } from "pages/common/lib/form";

const FormUtils = {
    webUrlRegex: /^(http(s)?):\/\/[\w\.-]+(\.[a-zA-Z]{2,})(:\d+)?\/?/,

    Validators: {
        required: function (fieldName: string) {
            return {
                type: `required`,
                message: `You must specify a ${fieldName}.`
            };
        },

        matchRegExp: function (regexp: RegExp, errorMessage: string) {
            return {
                type: `regexp`,
                regexp,
                flags: `i`,
                message: errorMessage
            };
        },

        minLength: function (
            fieldName: string,
            fieldLabel: string,
            minLength: number,
            doNotDisplay?: boolean
        ) {
            return function (value: string) {
                const err = {
                    type: fieldName,
                    message: `${fieldLabel} too short, it must have ${minLength} or more characters.`,
                    doNotDisplay: doNotDisplay === true ? true : false
                };
                if (value?.length !== 0 && value?.length < minLength) {
                    return err;
                }
            };
        },

        maxLength: function (
            fieldName: string,
            fieldLabel: string,
            maxLength: number,
            doNotDisplay?: boolean
        ) {
            return function (value: string) {
                const err = {
                    type: fieldName,
                    message: `${fieldLabel} can't be over ${maxLength} characters.`,
                    doNotDisplay: doNotDisplay === true ? true : false
                };
                if (value?.length !== 0 && value?.length > maxLength) {
                    return err;
                }
            };
        },

        restrictLength: function (
            fieldName: string,
            fieldLabel: string,
            minLength: number,
            maxLength: number
        ) {
            return function (value: string) {
                const err = {
                    type: fieldName,
                    message: `${fieldLabel} must be between ${minLength} and ${maxLength} characters.`
                };
                if (
                    value.length !== 0 &&
                    (value.length < minLength || value.length > maxLength)
                )
                    return err;
            };
        },

        match: function (
            fieldName: string,
            matchFieldKey: string,
            fieldLabel: string
        ) {
            return function (
                value: string,
                formsValues: Record<string, string>
            ) {
                const err = {
                    type: fieldName,
                    message: `${fieldLabel} do not match.`
                };
                if (value !== formsValues[matchFieldKey]) return err;
            };
        },

        range: function (fieldName: string, min: number, max: number) {
            return {
                type: `range`,
                min,
                max,
                message: `${fieldName} must be a number between ${min} and ${max}.`
            };
        },

        campaignRequired: function () {
            return function (
                value: string,
                formValues: Record<string, string>
            ) {
                if (formValues.campaignEnabled) {
                    const err = {
                        type: `contextId`,
                        message: `You need to select a campaign.`
                    };
                    if (!_.isString(value) || value.indexOf(`:`) === -1) {
                        return err;
                    }
                }
            };
        },

        projectRequired: function () {
            return function (
                value: string,
                formValues: Record<string, string>
            ) {
                if (formValues.editorEnabled) {
                    const err = {
                        type: `projectId`,
                        message: `You need to select a project.`
                    };
                    if (!_.isString(value) || _.isEmpty(value)) {
                        return err;
                    }
                }
            };
        },

        streamUrl: function () {
            return function (value: string) {
                if (!isValidStreamUrl(value)) {
                    return {
                        type: `url`,
                        message: `Please enter a valid URL.`
                    };
                }
            };
        },

        youtubeTags: function (fieldName: string, fieldLabel: string) {
            return function (value: string) {
                const err = {
                    type: fieldName,
                    message: `${fieldLabel} too long, it must have 500 or fewer characters.`
                };

                // calculate number of characters (youtube adds 2 quote marks for every space)
                // See: http://stackoverflow.com/questions/24162423/youtube-data-api-v3-does-not-support-500-characters-for-tags-property-of-video
                if (value && value.length !== 0) {
                    const numberOfSpaces = value.replace(/[^\s]/g, ``).length;
                    const numberOfChars = value.length + numberOfSpaces * 2;

                    if (numberOfChars > 500) {
                        return err;
                    }
                }
            };
        },

        rtmpUrl: function () {
            return {
                type: `regexp`,
                regexp: rtmpRegex,
                flags: `i`,
                message: `Must be a valid RTMP URL`
            };
        },

        timeFormatHHMMSS: () => {
            return {
                type: `regexp`,
                regexp: new RegExp(
                    /^[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]{1,3})?$/
                ),
                message: `Please enter the time in the format \`HH:MM:SS\``
            };
        },

        durationLongerThan: function (fieldName: string, toCompare: number) {
            return function (value: number) {
                if (value > toCompare) {
                    return {
                        type: fieldName,
                        message: `Time must be before the end of the video`
                    };
                }
            };
        },

        longerThanField: function (
            fieldName: string,
            form: {
                fields: Record<string, { editor: { getValue: () => string } }>;
            },
            fieldToCompare: string,
            fieldToCompareLabel: string
        ) {
            return function (value: string) {
                const valueToBeLongerThan =
                    //@ts-ignore: not sure this is used anymore
                    form.fields[fieldToCompare].editor.getValue();
                if (value <= valueToBeLongerThan) {
                    return {
                        type: fieldName,
                        message: `Must be after ${fieldToCompareLabel}`
                    };
                }
            };
        },

        shorterThanField: function (
            fieldName: string,
            form: {
                fields: Record<string, { editor: { getValue: () => string } }>;
            },
            fieldToCompare: string,
            fieldToCompareLabel: string
        ) {
            return function (value: string) {
                const valueToBeShorterThan =
                    form.fields[fieldToCompare].editor.getValue();
                if (value >= valueToBeShorterThan) {
                    return {
                        type: fieldName,
                        message: `Must be before ${fieldToCompareLabel}`
                    };
                }
            };
        },

        webUrl: function () {
            return {
                type: `regexp`,
                regexp: FormUtils.webUrlRegex,
                flags: `i`,
                message: `Must be a valid URL`
            };
        },

        shareTitleRegex: () => {
            return {
                type: `regexp`,
                regexp: new RegExp(/^[^<>]+$/),
                message: `The title cannot contain '<' or '>' characters.`
            };
        },

        twitterEmojiValidator: (type: string) => {
            return function (value: string, formValues: Record<string, any>) {
                if (
                    formValues.socialCredentials.some((cred: any) =>
                        cred.isTwitterAccount()
                    )
                ) {
                    const text = formValues[type];
                    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape
                    if (
                        text?.match(/\p{Extended_Pictographic}/gu) ||
                        text?.match(/[\uD400-\uDFFF]/g) // https://en.wikipedia.org/wiki/Mathematical_Alphanumeric_Symbols
                    ) {
                        return {
                            type,
                            message: `Emojis and formatting are no longer supported in titles or descriptions on the X (Twitter) platform`
                        };
                    }
                }
            };
        },

        durationBiggerThan: function (fieldName: string, fieldLabel: string) {
            return function (value: string) {
                const durationInMs = parseInt(value, 10) * 60 * 1000;
                const mainVideoLayer =
                    //@ts-ignore: not sure this is used anymore
                    App.Models.project.config.layers.first().sections;

                if (durationInMs < mainVideoLayer.getDuration()) {
                    return {
                        type: fieldName,
                        message: `${fieldLabel} can't be shorter that the duration of all the current clips`
                    };
                }
            };
        },

        socialMessageValidator: function (fieldName: string) {
            return function (value: string, formValues: Record<string, any>) {
                if (
                    //TODO: need types for social credentials, sadly this are backbone atm
                    formValues.socialCredentials.some(
                        (cred: any) =>
                            cred.isSocialMedia() &&
                            !cred.isTikTokAccount() &&
                            !cred.isInstagramAccount()
                    )
                ) {
                    // If a social credential has been selected the user has to input a
                    // socialMessage or a description, otherwise he can't share
                    if (!formValues.socialMessage?.length) {
                        return {
                            type: fieldName,
                            message: `Be social. Write a message before sharing.`
                        };
                    }
                }
            };
        },

        twitterSponsorshipProgramIdValidator: function (
            value: string,
            formValues: Record<string, string>
        ) {
            if (
                formValues.twitterMonetizationType ===
                Constants.TWITTER_MONETIZATION.SPONSORSHIPS
            ) {
                if (!value) {
                    return {
                        type: `twitterSponsorshipProgramId`,
                        message: `You must specify a program for Sponsorships`
                    };
                }
            }
        },

        twitterSponsorshipProgramExpirationDateValidator: function (
            value: number,
            formValues: Record<string, string>
        ) {
            if (
                formValues.twitterMonetizationType ===
                    Constants.TWITTER_MONETIZATION.SPONSORSHIPS &&
                value &&
                moment(value, `h:mma, DD/MM/YYYY`).unix() * 1000 < Date.now()
            ) {
                return {
                    type: `required`,
                    message: `The scheduled expiration date cannot be before the current date and time. Please select a date in the future or clear the value entirely.`
                };
            }
        },

        twitterMonetizationVideoCategoryValidator: function (
            value: string,
            formValues: Record<string, string>
        ) {
            if (
                formValues.twitterMonetizationType ===
                    Constants.TWITTER_MONETIZATION.VIDEO_ADS &&
                value &&
                formValues.twitterBlacklistVideoCategoryIds
            ) {
                const chosenCategories = value.split(`,`);
                const chosenBlacklistCategories =
                    formValues.twitterBlacklistVideoCategoryIds.split(`,`);

                if (
                    _.intersection(chosenCategories, chosenBlacklistCategories)
                        .length > 0
                ) {
                    return {
                        type: `twitterVideoCategoryIds`,
                        message: `You cannot block categories you have chosen for In-Stream Video Ads`
                    };
                }
            }
        },

        twitterMonetizationTypeValidation: function (
            value: string,
            formValues: Record<string, string>
        ) {
            if (
                formValues.twitterMonetizationType ===
                    Constants.TWITTER_MONETIZATION.VIDEO_ADS &&
                !value
            ) {
                return {
                    type: `required`,
                    message: `Please select a video category for In-Stream Video Ads`
                };
            }
        },

        twitterInStreamAdsCountryIdsValidator: function (
            value: string,
            formValues: Record<string, string>
        ) {
            if (
                formValues.twitterMonetizationType ===
                    Constants.TWITTER_MONETIZATION.VIDEO_ADS &&
                !value
            ) {
                return {
                    type: `required`,
                    message: `Please select at least one option for geo-targeting for In-Stream Video Ads e.g. All Available Countries`
                };
            }
        },

        validJSON(value: string) {
            let isValid = true;
            try {
                JSON.parse(value);
            } catch (e) {
                isValid = false;
            }

            if (!_.isString(value) || _.isNull(value)) {
                isValid = false;
            }

            return isValid;
        },

        webSource() {
            return (value: string) => {
                if (FormUtils.webUrlRegex.test(value)) {
                    return;
                }

                // this is a "hidden" feature to facilitate client trials with third party integrations
                const isValidJSON = FormUtils.Validators.validJSON(value);
                if (!isValidJSON) {
                    return {
                        type: `url`,
                        message: `Please enter a valid URL.`
                    };
                }
            };
        },

        targetRequired: function () {
            return function (
                value: string,
                formValues: Record<string, string>
            ) {
                if (!formValues.editorEnabled && !formValues.campaignEnabled) {
                    return {
                        type: `required`
                    };
                }
            };
        },

        passwordRequirementsSatisfied: function () {
            return function (value: string) {
                const validationRequirements = getPasswordStrengthTests(value);
                for (const req in validationRequirements) {
                    if (!validationRequirements[req]) {
                        return {
                            type: `password`
                        };
                    }
                }
            };
        }
    },

    sanitizeObject(obj = {}) {
        for (const key in obj) {
            const value = obj[key];
            if (_.isNull(value) || _.isUndefined(value) || value === ``) {
                delete obj[key];
            }
        }
        return obj;
    },

    fetchAndSetOptionsOnEditor(fetchRequest, editor, key) {
        fetchRequest().done((res) => {
            editor.setOptionsWithoutClearingValues(res[key]);
        });
        // leaving it here for further tests, will remove later
        // editor.setOptionsWithoutClearingValues(fetchRequest());
    }
};
export default FormUtils;
